import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Star, PhoneCall } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const EMPTY = {
  name: "", relationship: "", phone: "", alt_phone: "", email: "", address: "",
  preferred_contact_method: "", is_primary_contact: false, is_emergency_contact: false,
  authorised_to_receive_info: false, notes: "",
};

export default function ParentsTab({ child }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("children:update");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/children/${child.id}/parents`);
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", relationship: p.relationship || "", phone: p.phone || "",
      alt_phone: p.alt_phone || "", email: p.email || "", address: p.address || "",
      preferred_contact_method: p.preferred_contact_method || "",
      is_primary_contact: p.is_primary_contact || false,
      is_emergency_contact: p.is_emergency_contact || false,
      authorised_to_receive_info: p.authorised_to_receive_info || false,
      notes: p.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (editing) {
        payload.status = editing.status;
        await api.patch(`/children/${child.id}/parents/${editing.id}`, payload);
        toast.success("Parent/carer updated");
      } else {
        await api.post(`/children/${child.id}/parents`, payload);
        toast.success("Parent/carer added");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/children/${child.id}/parents/${p.id}`, {
        name: p.name, status: p.status === "active" ? "inactive" : "active",
      });
      toast.success(p.status === "active" ? "Contact marked inactive" : "Contact reactivated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="parents-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Relational contacts — a parent/carer can be linked to more than one child.</p>
        {canManage && (
          <Button data-testid="parent-add-button" className="h-10 bg-primary hover:bg-brand-hover text-white font-semibold"
            onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Add parent/carer
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-600" data-testid="parents-empty">No parent/carer contacts recorded.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="parents-list">
          {items.map((p) => (
            <Card key={p.id} data-testid={`parent-card-${p.id}`} className={p.status === "inactive" ? "opacity-60" : ""}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900">{p.name}</p>
                    <p className="text-xs text-slate-500">{p.relationship || "—"}</p>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {p.is_primary_contact && (
                      <Badge className="bg-primary text-white border-transparent" data-testid={`parent-primary-${p.id}`}>
                        <Star className="h-3 w-3 mr-1" /> Primary contact
                      </Badge>
                    )}
                    {p.is_emergency_contact && (
                      <Badge className="bg-red-50 text-red-700 border-transparent" data-testid={`parent-emergency-${p.id}`}>
                        <PhoneCall className="h-3 w-3 mr-1" /> Emergency contact
                      </Badge>
                    )}
                    {p.status === "inactive" && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                </div>
                <dl className="text-sm text-slate-600 space-y-1">
                  {p.phone && <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Telephone</dt><dd>{p.phone}</dd></div>}
                  {p.alt_phone && <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Alt. phone</dt><dd>{p.alt_phone}</dd></div>}
                  {p.email && <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Email</dt><dd className="break-all">{p.email}</dd></div>}
                  {p.preferred_contact_method && <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Preferred</dt><dd>{p.preferred_contact_method}</dd></div>}
                  <div className="flex gap-2"><dt className="text-slate-500 w-28 shrink-0">Info sharing</dt><dd>{p.authorised_to_receive_info ? "Authorised" : "Not authorised"}</dd></div>
                </dl>
                {canManage && (
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" className="h-9 text-xs" data-testid={`parent-edit-${p.id}`} onClick={() => openEdit(p)}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" className="h-9 text-xs" data-testid={`parent-toggle-${p.id}`} onClick={() => toggleActive(p)}>
                      {p.status === "active" ? "Mark inactive" : "Reactivate"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit parent/carer" : "Add parent/carer"}</DialogTitle>
            <DialogDescription>Contact and authority details for this child's parent or carer.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="parent-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Full name *</Label>
                <Input data-testid="parent-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Relationship to child</Label>
                <Input data-testid="parent-relationship-input" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Telephone</Label>
                <Input data-testid="parent-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Alternative telephone</Label>
                <Input data-testid="parent-altphone-input" value={form.alt_phone} onChange={(e) => setForm({ ...form, alt_phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label>
                <Input data-testid="parent-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Preferred contact method</Label>
                <Select value={form.preferred_contact_method || "none"} onValueChange={(v) => setForm({ ...form, preferred_contact_method: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="parent-contact-method-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="text">Text message</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="letter">Letter</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label>Address (if different / required)</Label>
              <Input data-testid="parent-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="flex flex-wrap gap-4 text-sm">
              {[
                ["is_primary_contact", "Primary contact", "parent-flag-primary"],
                ["is_emergency_contact", "Emergency contact", "parent-flag-emergency"],
                ["authorised_to_receive_info", "Authorised to receive information", "parent-flag-authorised"],
              ].map(([key, label, testid]) => (
                <label key={key} className="flex items-center gap-2 min-h-[44px]">
                  <input type="checkbox" data-testid={testid} checked={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.checked })} className="h-4 w-4 accent-[#005EB8]" />
                  {label}
                </label>
              ))}
            </div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Textarea data-testid="parent-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button data-testid="parent-save-button" type="submit" disabled={saving}
                className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Add contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
